import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { CommunicationService } from '../../communication/communication.service';
import { ProjectCommunicationService } from '../../communication/project-comms.service';
import Util from './../../util';
import { TopbarProfileDialogComponent } from './topbar-profile-dialog/topbar-profile-dialog.component';
import { take, takeUntil } from 'rxjs/operators';
import { AnessaTranslationService } from '../../helper/translation/anessa-translation.service';
import Utils from './../../util';
import { UserpilotReloader } from '../../helper/userpilot-reloader.component';
import { PreferencesDialogComponent } from './preferences-dialog/preferences-dialog.component';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-dropdown-menu',
  templateUrl: './dropdown-menu.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class DropdownMenuComponent extends UserpilotReloader implements OnInit, OnDestroy {
  @Input() burger?: boolean;
  onDestroyNotifier = new EventEmitter();
  /** trigger for translation pipe */
  trigger?: string;

  constructor(
    private commService: CommunicationService,
    private projectCommService: ProjectCommunicationService,
    private authService: AuthService,

    private location: Location,
    private router: Router,
    private dialog: MatDialog,
    public translationService: AnessaTranslationService
  ) {
    super();
  }

  ngOnInit() {
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  printSummary() {
    this.router.navigateByUrl('project/print');
  }

  showProfile() {
    this.authService.getAccountInfo().pipe(take(1)).subscribe(
      (result: any) => {
        this.dialog.open(TopbarProfileDialogComponent, {
          width: '800px',
          height: '80%',
          data: result.obj,
        });
      }
    );
  }

  openProject() {
    this.router.navigateByUrl('/project/open').then(success => {
      if (success) {
        this.projectCommService.unloadProject();
      }
    });
  }

  closeProject() {
    this.router.navigateByUrl('/home').then(success => {
      if (success) {
        this.projectCommService.unloadProject();
      }
    });
  }

  saveProjectAs() {
    const dialog = this.dialog.open(TopbarSaveAsDialogComponent, {
      width: '500px',
    });
    dialog.afterClosed().pipe(take(1)).subscribe(
      result => {
        if (result) {
          this.projectCommService.saveProjectAs(result);
        }
      });
  }

  openDefaultPreferences(): void {
    this.dialog.open(PreferencesDialogComponent, {
      width: '600px'
    });
  }

  deleteProject() {
    if (Util.isProjectLoaded()) {
      const dialog = this.dialog.open(TopbarSaveDialogComponent, {
        width: '500px',
        data: {
          title: this.translationService.get("DROPDOWN.DELETE"),
          content: this.translationService.get("DROPDOWN.DEL_CONTENT"),
          save: this.translationService.get("DROPDOWN.DELETE"),
          noSave: this.translationService.get("PROJECT.COMMON.CANCEL"),
        }
      });
      dialog.afterClosed().pipe(take(1)).subscribe(
        result => {
          if (result === true) {
            this.deleteConfirm();
          }
        }
      );
    }
  }

  downloadProject(project: any) {
    project.accountID = undefined;
    project._id = undefined;

    if (project.results && project.results.length > 0) {
      project.results = project.results[0];
      project.results._id = undefined;
      project.results.plantid = undefined;
    }

    const projectFile = new Blob([JSON.stringify(project)], { type: 'text/json' });
    const url = window.URL.createObjectURL(projectFile);
    const link = document.createElement('a');
    link.download = `${project.projectInfo.title}.ADA`;
    link.href = url;
    link.click();
  }

  makeExportRequest(projectId: string) {
    return (this.commService.getRequest(projectId, '/wsasapi/project/export'
    ) as Observable<{obj: {
      project: any,
      results: any
    }}>)
  }

  exportProject() {
    let projectId = this.projectCommService.getProjectId();
    if (!projectId) return;
    this.makeExportRequest(projectId)
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(take(1))
      .subscribe(data => {
        const project = data.obj.project;
        project['results'] = data.obj.results;
        this.downloadProject(project);
      });
  }

  deleteConfirm() {
    localStorage.setItem('delete', 'true');
    this.projectCommService.deleteProject();
    this.router.navigateByUrl('/home');
  }

  newProject() {
    if (this.limitToScreeningProjects()) return;
    this.router.navigateByUrl('/project/new').then(success => {
      if (success) {
        this.projectCommService.unloadProject();
      }
    });
  }

  newInitialInformation() {
    this.router.navigateByUrl('/project-input').then(success => {
      if (success) {
        this.projectCommService.unloadProject();
      }
    });
  }

  openArchived() {
    this.router.navigateByUrl('/project/archived').then(success => {
      if (success) {
        this.projectCommService.unloadProject();
      }
    });
  }

  onLogout() {
    const canAccessADM = Utils.canAccessADM();
    const canAccessADO = Utils.canAccessADO();
    if (canAccessADM || canAccessADO) {
      const dialogRef = this.dialog.open(TopbarSaveDialogComponent, {
        width: '500px',
        data: {
          title: "Log Out",
          content: "This will log you out of all anessa products. Continue?",
          save: "log out",
          noSave: "stay",
        }
      });
      dialogRef.afterClosed().pipe(take(1)).subscribe(logout => {
        if (logout) {
          this.authService.logout();
        }
      });
    } else {
      this.authService.logout();
    }
  }


  openAccountAdmin() {
    this.router.navigateByUrl('/accadmin');
  }

  isAccountAdmin() {
    return Util.isAccountAdmin();
  }

  isDBAdmin() {
    return Util.isDBAdmin();
  }

  isInitialFormUser() {
    return Util.isInitialFormUser();
  }

  isProjectLoaded() {
    return Util.isProjectLoaded();
  }

  isProjectInput() {
    return this.location.path() === '/project-input';
  }

  canViewHelp() {
    return Util.canViewHelp();
  }

  openHelp() {
    this.router.navigateByUrl('/help');
  }

  canDeleteProject() {
    return Util.canDeleteProject();
  }

  canViewProjectInput() {
    return Util.canViewProjectInput();
  }

  canViewArchived() {
    return Util.hasInputListAccess();
  }

  showNewInitialFormButton() {
    return this.canViewProjectInput() && !this.isInitialFormUser() && this.location.path() !== '/project-input';
  }

  getName() {
    return localStorage.getItem('userFirstName')?.toUpperCase() + ' ' +
      localStorage.getItem('userLastName')?.toUpperCase();
  }

  limitToScreeningProjects() {
    return Utils.limitToScreeningProjects();
  }

  isProjectLimitReached() {
    return Utils.isProjectLimitReached();
  }

  isScreeningProjectLimitReached() {
    return Utils.isScreeningProjectLimitReached();
  }
}

@Component({
  selector: 'topbar-save-dialog',
  templateUrl: './topbar-save-dialog/topbar-save-dialog.html',
  styleUrls: [
    './topbar-save-dialog/topbar-save-dialog.scss'
  ]
})
export class TopbarSaveDialogComponent {
  constructor(public dialogRef: MatDialogRef<TopbarSaveDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
  }

  title?: string;

  cancel() {
    this.dialogRef.close(null);
  }

  save() {
    this.dialogRef.close(true);
  }
}

@Component({
  selector: 'app-topbar-save-as-dialog',
  templateUrl: './topbar-save-as-dialog/topbar-save-as-dialog.component.html',
  styleUrls: [
    './topbar-save-as-dialog/topbar-save-as-dialog.component.css'
  ]
})
export class TopbarSaveAsDialogComponent {
  constructor(public dialogRef: MatDialogRef<TopbarConfirmDialogComponent>,
              public comService: CommunicationService,
              public projComService: ProjectCommunicationService
  ) { }

  title?: string;
  projectFiles: { id: string, projectTitle: string }[] = [];

  onSave() {
    const unique = this.projComService.checkUniqueProjectTitle(this.title);
    if (unique) {
      this.dialogRef.close(this.title);
    }
  }

  onCancel() {
    this.dialogRef.close(null);
  }

}

@Component({
  selector: 'topbar-confirm-dialog',
  templateUrl: './topbar-confirm-dialog/topbar-confirm-dialog.html',
  styleUrls: [
    './topbar-confirm-dialog/topbar-confirm-dialog.scss'
  ]
})
export class TopbarConfirmDialogComponent {
  constructor(public dialogRef: MatDialogRef<TopbarConfirmDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
  }

  title?: string;

  cancel() {
    this.dialogRef.close(null);
  }

  noSave() {
    this.dialogRef.close(false);
  }

  save() {
    this.dialogRef.close(true);
  }
}

@Component({
  selector: 'topbar-admin-dialog',
  templateUrl: './topbar-admin-dialog/topbar-admin-dialog.html',
  styleUrls: [
    './topbar-admin-dialog/topbar-admin-dialog.scss'
  ]
})
export class TopbarAdminDialogComponent {
  constructor(public dialogRef: MatDialogRef<TopbarAdminDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
  }

  title?: string;

  cancel() {
    this.dialogRef.close(null);
  }

  noSave() {
    this.dialogRef.close(false);
  }

  save() {
    this.dialogRef.close(true);
  }
}
